import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router";
import { useLoader } from "../../../../../../context";
import arrayToObject from "../../../../../../utils/arrayToObject";
import { getDronesList } from "../../../../../../Service/Api/drone";

const LIMIT = 25;

const useDronesList = (refreshData = null) => {
  const { campusId } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const [availableDronesList, setAvailableDronesList] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  // Reset when campus changes
  useEffect(() => {
    setAvailableDronesList({});
    setPage(1);
    setHasMore(true);
    setError(null);
  }, [campusId, refreshData]);

  const fetchDrones = useCallback(async () => {
    if (!campusId || !hasMore || isFetching) return;

    setIsFetching(true);
    page === 1 && showLoader();

    try {
      const res = await getDronesList({
        campus_id: campusId,
        unassigned: true,
        limit: LIMIT,
        page,
      });

      const rows = res?.rowData || [];

      setAvailableDronesList((prev) => {
        const obj = arrayToObject(rows, "device_id", { isUsed: false });
        const newList = { ...prev, ...obj };
        return newList;
      });

      // 🔑 THIS controls auto-pagination
      if (rows.length < LIMIT) {
        setHasMore(false); // stop
      } else {
        setPage((p) => p + 1); // fetch next page
      }
    } catch (err) {
      setError(err);
      setHasMore(false);
    } finally {
      setIsFetching(false);
      hideLoader();
    }
  }, [campusId, page, hasMore, isFetching]);

  // 🔁 Auto-fetch until hasMore = false
  useEffect(() => {
    fetchDrones();
  }, [fetchDrones]);

  const setAvailableDrone = useCallback(
    (droneId, status = false) => {
      setAvailableDronesList((prev) => {
        const temp = { ...prev };
        if (!!temp[droneId]) {
          temp[droneId].isUsed = status;
        }
        return temp;
      });
    },
    [setAvailableDronesList],
  );

  return {
    hasMoreDronesList: hasMore,
    isFetching,
    error,
    availableDronesList,
    setAvailableDrone,
    showAutomaticDrones: Object.values(availableDronesList).length,
  };
};

export default useDronesList;
